import React, { useState } from 'react';
import { Input, Button, Space, Card, InputNumber, Select, Table, Empty, message } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePWJ30AQuery } from './hooks/usePWJ30AQuery';
import type { PrintRecord } from '../../services/pwj30aApi';
import { pwj30aApi } from '../../services/pwj30aApi';
import './PWJ30AForm.css';

const PWJ30AForm: React.FC = () => {
  const { loading, result, queried, noData, runBuildPrint, reset } = usePWJ30AQuery();

  const [productCode1, setProductCode1] = useState('');
  const [productCode2, setProductCode2] = useState('');
  const [processCode, setProcessCode] = useState('');
  const [processDesc, setProcessDesc] = useState('');
  const [productDesc, setProductDesc] = useState('');
  const [widthMin, setWidthMin] = useState<number | null>(null);
  const [widthMax, setWidthMax] = useState<number | null>(null);
  const [gsmSqm, setGsmSqm] = useState<number | null>(null);
  const [skipCount, setSkipCount] = useState<number>(0);
  const [printCopies, setPrintCopies] = useState<number>(1);
  const [labelFormat, setLabelFormat] = useState<number>(1);

  const handleLookupProduct = async () => {
    if (!productCode1.trim() || !productCode2.trim()) {
      message.warning('請輸入產品代號前後兩段');
      return;
    }
    try {
      const resp = await pwj30aApi.lookupProduct(productCode1.trim(), productCode2.trim());
      if (resp.found && resp.data) {
        setProductDesc(resp.data.J301_11 || '');
        setWidthMin(resp.data.J301_14 ?? null);
        setWidthMax(resp.data.J301_15 ?? null);
        setGsmSqm(resp.data.J301_16 ?? null);
      } else {
        message.warning(resp.message || '查無此產品代號!');
        setProductDesc('');
        setWidthMin(null);
        setWidthMax(null);
        setGsmSqm(null);
      }
    } catch {
      message.error('查詢產品資料失敗');
    }
  };

  const handleLookupProcess = async () => {
    if (!processCode.trim()) return;
    try {
      const resp = await pwj30aApi.lookupProcess(processCode.trim());
      if (resp.found && resp.data) {
        setProcessDesc(resp.data.desc || '');
      } else {
        message.warning(resp.message || '查無此加工說明代號');
        setProcessDesc('');
      }
    } catch {
      message.error('查詢加工說明失敗');
    }
  };

  const handleBuildPrint = () => {
    if (!productCode1.trim() || !productCode2.trim()) {
      message.warning('請輸入產品代號');
      return;
    }
    runBuildPrint({
      J301_01: productCode1.trim(),
      J301_02: productCode2.trim(),
      J301_17: processCode.trim() || undefined,
      J301_11: productDesc.trim() || undefined,
      J301_14: widthMin ?? undefined,
      J301_15: widthMax ?? undefined,
      J301_16: gsmSqm ?? undefined,
      J301_18: processDesc.trim() || undefined,
      skipCount: skipCount || 0,
      printCopies: printCopies || 1,
      labelFormat: labelFormat || 1,
    });
  };

  const handleReset = () => {
    setProductCode1('');
    setProductCode2('');
    setProcessCode('');
    setProcessDesc('');
    setProductDesc('');
    setWidthMin(null);
    setWidthMax(null);
    setGsmSqm(null);
    setSkipCount(0);
    setPrintCopies(1);
    setLabelFormat(1);
    reset();
  };

  const handlePrint = () => {
    if (!result || result.records.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>小嘜頭列印</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:12px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:3px 6px}th{background:#f0f0f0;text-align:center}@media print{body{margin:10mm}}</style></head><body>
<h2>小嘜頭列印</h2>
<p>格式：${result.labelFormat}　份數：${result.printCopies}　跳過：${result.skipCount}　總筆數：${result.totalCount}</p>
<table><thead><tr><th>產品代號1</th><th>產品代號2</th><th>列印內容</th><th>空白</th></tr></thead><tbody>
${result.records.map(r => `<tr><td>${r.J301_01}</td><td>${r.J301_02}</td><td>${r.J301_11}</td><td>${r.isBlank ? '是' : ''}</td></tr>`).join('')}
</tbody></table></body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<PrintRecord> = [
    { title: '產品代號1', dataIndex: 'J301_01', key: 'J301_01', width: 100 },
    { title: '產品代號2', dataIndex: 'J301_02', key: 'J301_02', width: 100 },
    { title: '列印內容', dataIndex: 'J301_11', key: 'J301_11', ellipsis: true },
    { title: '空白', dataIndex: 'isBlank', key: 'isBlank', width: 60, render: (v: boolean) => v ? '是' : '' },
  ];

  return (
    <PermissionGuard module={Modules.PWJ30A} action="query">
      <div className="pwj30a-container">
        <div className="pwj30a-header">
          <h2>小嘜頭列印</h2>
          {queried && !noData && result && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap size="middle">
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>產品代號 (前段)</label>
                <Input
                  value={productCode1}
                  onChange={e => setProductCode1(e.target.value)}
                  onBlur={handleLookupProduct}
                  placeholder="6碼" style={{ width: 110 }} allowClear maxLength={6}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>產品代號 (後段)</label>
                <Input
                  value={productCode2}
                  onChange={e => setProductCode2(e.target.value)}
                  onBlur={handleLookupProduct}
                  placeholder="6碼" style={{ width: 110 }} allowClear maxLength={6}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>加工說明代號</label>
                <Input
                  value={processCode}
                  onChange={e => setProcessCode(e.target.value)}
                  onBlur={handleLookupProcess}
                  placeholder="4碼" style={{ width: 100 }} allowClear maxLength={4}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>加工說明</label>
                <Input value={processDesc} onChange={e => setProcessDesc(e.target.value)} placeholder="自動帶入" style={{ width: 200 }} />
              </div>
            </Space>
            <Space wrap size="middle">
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>品名描述</label>
                <Input value={productDesc} onChange={e => setProductDesc(e.target.value)} placeholder="自動帶入或手動輸入" style={{ width: 220 }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>幅寬最小 (CM)</label>
                <InputNumber value={widthMin} onChange={v => setWidthMin(v)} style={{ width: 110 }} min={0} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>幅寬最大 (INCH)</label>
                <InputNumber value={widthMax} onChange={v => setWidthMax(v)} style={{ width: 110 }} min={0} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>GSM/SQM</label>
                <InputNumber value={gsmSqm} onChange={v => setGsmSqm(v)} style={{ width: 110 }} min={0} />
              </div>
            </Space>
            <Space wrap size="middle">
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>跳過空白數</label>
                <InputNumber value={skipCount} onChange={v => setSkipCount(v ?? 0)} style={{ width: 90 }} min={0} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>列印份數</label>
                <InputNumber value={printCopies} onChange={v => setPrintCopies(v ?? 1)} style={{ width: 90 }} min={1} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>標籤格式</label>
                <Select value={labelFormat} onChange={setLabelFormat} style={{ width: 100 }}>
                  <Select.Option value={1}>格式 1</Select.Option>
                  <Select.Option value={2}>格式 2</Select.Option>
                </Select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
                <Space>
                  <Button type="primary" icon={<SearchOutlined />} onClick={handleBuildPrint} loading={loading}>建立列印</Button>
                  <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
                </Space>
              </div>
            </Space>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && result && (
          <>
            <div style={{ marginBottom: 8, fontSize: 13, color: '#555' }}>
              格式：{result.labelFormat}　份數：{result.printCopies}　跳過：{result.skipCount}　總筆數：{result.totalCount}
            </div>
            <Table<PrintRecord>
              columns={columns}
              dataSource={result.records}
              rowKey={(r, i) => `${r.J301_01}-${r.J301_02}-${i}`}
              loading={loading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 600 }}
            />
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default PWJ30AForm;
